import { AlertTriangle, BookOpen, Check, Coffee, Eye, Globe, Hand, Laptop, MessageSquare, School } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { activityDetail, clock, dayLabel } from '../lib/format';
import type { Activity, Child } from '../lib/types';
import { useParent } from './ParentContext';

const ICONS: Record<string, React.ReactNode> = {
  site: <Globe size={16} />,
  alert: <AlertTriangle size={16} />,
  chat: <MessageSquare size={16} />,
  session: <School size={16} />,
  watch: <Eye size={16} />,
  device: <Laptop size={16} />,
  request: <Hand size={16} />,
  break: <Coffee size={16} />,
  submit: <Check size={16} />,
  reading: <BookOpen size={16} />,
};

const FILTERS = [
  { id: 'all', label: 'Everything' },
  { id: 'alert', label: 'Alerts' },
  { id: 'site', label: 'Websites' },
  { id: 'talk', label: 'Messages' },
] as const;

export function ActivityList({ child }: { child: Child }) {
  const { socket } = useParent();
  const [items, setItems] = useState<Activity[]>([]);
  const [more, setMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<(typeof FILTERS)[number]['id']>('all');

  const load = useCallback(
    async (before?: number) => {
      setLoading(true);
      const page = await api.get<Activity[]>(`/api/children/${child.id}/activity?limit=50${before ? `&before=${before}` : ''}`);
      setItems((list) => (before ? [...list, ...page] : page));
      setMore(page.length === 50);
      setLoading(false);
    },
    [child.id],
  );

  useEffect(() => {
    load().catch(() => setLoading(false));
  }, [load]);

  useEffect(
    () =>
      socket?.on('activity', (m) => {
        const item = m.item as Activity;
        if (item.childId === child.id) setItems((list) => (list.some((i) => i.id === item.id) ? list : [item, ...list]));
      }),
    [socket, child.id],
  );

  const shown = items.filter((i) =>
    filter === 'all' ? true : filter === 'talk' ? ['request', 'chat'].includes(i.kind) : i.kind === filter,
  );

  let lastDay = '';
  return (
    <section className="card">
      <div className="filters">
        {FILTERS.map((f) => (
          <button key={f.id} className={`chip ${filter === f.id ? 'active' : ''}`} onClick={() => setFilter(f.id)}>
            {f.label}
          </button>
        ))}
      </div>
      {!loading && !shown.length && (
        <p className="muted">
          Nothing here yet. Websites appear once the browser extension is paired, and only during school time.
        </p>
      )}
      <ol className="timeline">
        {shown.map((i) => {
          const day = dayLabel(i.ts);
          const header = day !== lastDay ? <li className="day">{(lastDay = day)}</li> : null;
          return [
            header,
            <li key={i.id} className={`event event-${i.kind}`}>
              <span className="event-icon">{ICONS[i.kind] ?? <Globe size={16} />}</span>
              <div className="grow">
                <div className="event-title">{i.title}</div>
                {activityDetail(i.kind, i.detail) && <div className="muted small">{activityDetail(i.kind, i.detail)}</div>}
              </div>
              <time className="muted small">{clock(i.ts)}</time>
            </li>,
          ];
        })}
      </ol>
      {more && items.length > 0 && (
        <button className="btn wide" disabled={loading} onClick={() => load(items[items.length - 1]!.id)}>
          {loading ? 'Loading…' : 'Show earlier'}
        </button>
      )}
    </section>
  );
}
